import { useCallback, useMemo } from "react";
import { useCookieConsent } from "react-cookie-manager";
import type { ConsentCategory } from "./types";

type CookieCategoryKey = "Analytics" | "Social" | "Advertising";

const CATEGORY_MAP: Record<Exclude<ConsentCategory, "necessary">, CookieCategoryKey> = {
  functional: "Social",
  analytics: "Analytics",
  marketing: "Advertising",
};

const DEFAULT_PREFERENCES: Record<ConsentCategory, boolean> = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
};

export function useConsent() {
  const { hasConsent, isDeclined, detailedConsent, acceptCookies, declineCookies, updateDetailedConsent } = useCookieConsent();

  const preferences = useMemo(() => {
    if (!detailedConsent) return DEFAULT_PREFERENCES;

    return {
      necessary: true,
      functional: detailedConsent.Social?.consented ?? false,
      analytics: detailedConsent.Analytics?.consented ?? false,
      marketing: detailedConsent.Advertising?.consented ?? false,
    };
  }, [detailedConsent]);

  const isAllowed = useCallback(
    (category: ConsentCategory) => {
      if (category === "necessary") return true;
      const key = CATEGORY_MAP[category];
      if (!detailedConsent) {
        // If user hasn't made a granular choice but has accepted once, treat as allowed.
        return Boolean(hasConsent);
      }
      return detailedConsent[key]?.consented ?? false;
    },
    [detailedConsent, hasConsent],
  );

  const setPreference = useCallback(
    (category: ConsentCategory, value: boolean) => {
      if (category === "necessary") {
        return;
      }

      const current = {
        Analytics: detailedConsent?.Analytics?.consented ?? false,
        Social: detailedConsent?.Social?.consented ?? false,
        Advertising: detailedConsent?.Advertising?.consented ?? false,
      };

      const key = CATEGORY_MAP[category];
      current[key] = value;
      updateDetailedConsent(current);
    },
    [detailedConsent, updateDetailedConsent],
  );

  return {
    hasConsent,
    isDeclined,
    preferences,
    acceptAll: acceptCookies,
    declineAll: declineCookies,
    isAllowed,
    setPreference,
  };
}

export type ConsentState = ReturnType<typeof useConsent>;
